import {
  createIndividualObligation,
  getActs,
  getConcepts,
  getFixedRules,
  getMembers,
  IndividualObligationApiError,
  type ActContract,
  type ConceptContract,
  type FixedRuleContract,
  type IndividualObligationContract,
  type MemberListItemContract,
} from '@urbanik/api-client';
import {
  Button,
  CanonicalCard,
  CanonicalDefinitionList,
  CanonicalNotice,
  EmptyState,
  FormActions,
  SearchSelectField,
  SelectField,
  StatePanel,
  TextField,
  canonicalStates,
} from '@urbanik/ui';
import { BadgeDollarSign, CheckCircle2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { apiBase } from './api-base';

type Origin = 'rule' | 'act';
const options = (items: readonly { id: string; label: string }[]) =>
  items.map((item) => ({ value: item.id, label: item.label }));

export function IndividualObligationForm({ canCreate }: { canCreate: boolean }) {
  const [members, setMembers] = useState<readonly MemberListItemContract[]>([]);
  const [selectedMember, setSelectedMember] = useState<MemberListItemContract | null>(null);
  const [memberQuery, setMemberQuery] = useState('');
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [concepts, setConcepts] = useState<readonly ConceptContract[]>([]);
  const [acts, setActs] = useState<readonly ActContract[]>([]);
  const [rules, setRules] = useState<readonly FixedRuleContract[]>([]);
  const [loading, setLoading] = useState(true);
  const [origin, setOrigin] = useState<Origin>('rule');
  const [memberId, setMemberId] = useState('');
  const [period, setPeriod] = useState('');
  const [ruleId, setRuleId] = useState('');
  const [actId, setActId] = useState('');
  const [conceptId, setConceptId] = useState('');
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<IndividualObligationContract | null>(null);
  const [notice, setNotice] = useState<{ tone: 'error' | 'success'; message: string } | null>(null);

  useEffect(() => {
    if (!canCreate) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    Promise.all([
      getConcepts(apiBase, controller.signal),
      getActs(apiBase, controller.signal),
      getFixedRules(apiBase, controller.signal),
    ])
      .then(([conceptResult, actResult, ruleResult]) => {
        const validActs = actResult.items.filter(
          (item) => item.documentStatus === 'approved' && !item.deletedAt,
        );
        setConcepts(conceptResult.items.filter((item) => item.active));
        setActs(validActs);
        setRules(
          ruleResult.items.filter(
            (item) =>
              item.isCurrent && !item.deletedAt && validActs.some((act) => act.id === item.act.id),
          ),
        );
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setNotice({
          tone: 'error',
          message: error instanceof Error ? error.message : 'No pudimos cargar los catálogos.',
        });
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [canCreate]);

  useEffect(() => {
    if (!canCreate) return;
    const controller = new AbortController();
    setLoadingMembers(true);
    getMembers(
      apiBase,
      {
        query: memberQuery,
        status: 'active',
        sort: 'name',
        direction: 'asc',
        page: 1,
        pageSize: 20,
      },
      controller.signal,
    )
      .then((response) => setMembers(response.items))
      .catch((error) => {
        if (controller.signal.aborted) return;
        setNotice({
          tone: 'error',
          message: error instanceof Error ? error.message : 'No pudimos buscar socios.',
        });
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingMembers(false);
      });
    return () => controller.abort();
  }, [canCreate, memberQuery]);

  if (!canCreate)
    return <StatePanel state={canonicalStates.find((item) => item.kind === 'forbidden')!} />;
  if (loading)
    return <StatePanel state={canonicalStates.find((item) => item.kind === 'loading')!} />;
  if (!rules.length && !acts.length)
    return (
      <EmptyState
        title="No hay respaldos habilitados"
        description="Se requiere al menos una regla vigente o un acta aprobada y activa."
      />
    );

  const memberOptions = [
    ...(selectedMember && !members.some((member) => member.id === selectedMember.id)
      ? [selectedMember]
      : []),
    ...members,
  ].map((member) => ({
    value: member.id,
    label: `${member.familyNames} ${member.givenNames}`,
    meta: `Cédula ${member.nationalId}${member.slotNumber ? ` · Cupo ${member.slotNumber}` : ''}`,
  }));

  const save = async () => {
    setSaving(true);
    setResult(null);
    try {
      const response = await createIndividualObligation(apiBase, {
        memberId,
        period,
        origin:
          origin === 'rule' ? { type: 'rule', ruleId } : { type: 'act', actId, conceptId, amount },
      });
      setResult(response.obligation);
      setNotice({ tone: 'success', message: 'Obligación individual confirmada y auditada.' });
    } catch (error) {
      setNotice({
        tone: 'error',
        message:
          error instanceof IndividualObligationApiError
            ? error.message
            : 'No pudimos crear la obligación.',
      });
    } finally {
      setSaving(false);
    }
  };
  const disabled =
    saving ||
    !memberId ||
    !period ||
    (origin === 'rule' ? !ruleId : !actId || !conceptId || !amount);

  return (
    <section aria-label="Crear obligación individual">
      <CanonicalNotice
        message={notice?.message ?? ''}
        tone={notice?.tone ?? 'error'}
        onDismiss={() => setNotice(null)}
      />
      <CanonicalCard
        title="Nueva obligación extraordinaria"
        icon={<BadgeDollarSign aria-hidden="true" />}
      >
        <div className="obligation-form-grid">
          <SearchSelectField
            required
            label="Socio"
            hint="Busca por nombre, cédula o número de cupo y selecciona un resultado."
            placeholder="Buscar socio…"
            emptyMessage="No hay socios activos que coincidan con la búsqueda."
            loading={loadingMembers}
            value={memberId}
            onQueryChange={setMemberQuery}
            onValueChange={(nextId) => {
              setMemberId(nextId);
              setSelectedMember(members.find((member) => member.id === nextId) ?? null);
            }}
            options={memberOptions}
          />
          <TextField
            required
            label="Período"
            type="month"
            value={period}
            onChange={(event) => setPeriod(event.currentTarget.value)}
          />
          <SelectField
            required
            label="Origen"
            value={origin}
            onValueChange={(value) => setOrigin(value as Origin)}
            options={[
              { value: 'rule', label: 'Regla vigente' },
              { value: 'act', label: 'Acta aprobada' },
            ]}
          />
          {origin === 'rule' ? (
            <SelectField
              required
              label="Regla"
              value={ruleId}
              placeholder="Selecciona una regla"
              onValueChange={setRuleId}
              options={options(
                rules.map((item) => ({
                  id: item.id,
                  label: `${item.code} · ${item.concept.name} · USD ${item.amount} · ${item.act.number}`,
                })),
              )}
            />
          ) : (
            <>
              <SelectField
                required
                label="Acta"
                value={actId}
                placeholder="Selecciona un acta"
                onValueChange={setActId}
                options={options(
                  acts.map((item) => ({ id: item.id, label: `${item.number} · ${item.subject}` })),
                )}
              />
              <SelectField
                required
                label="Concepto"
                value={conceptId}
                placeholder="Selecciona un concepto"
                onValueChange={setConceptId}
                options={options(
                  concepts.map((item) => ({ id: item.id, label: `${item.code} · ${item.name}` })),
                )}
              />
              <TextField
                required
                label="Importe USD"
                inputMode="decimal"
                placeholder="0.00"
                value={amount}
                onChange={(event) => setAmount(event.currentTarget.value)}
              />
            </>
          )}
          <FormActions>
            <Button
              variant="primary"
              icon={<CheckCircle2 />}
              disabled={disabled}
              onClick={() => void save()}
            >
              {saving ? 'Confirmando…' : 'Crear y confirmar obligación'}
            </Button>
          </FormActions>
        </div>
      </CanonicalCard>
      {result ? (
        <CanonicalDefinitionList
          label="Obligación creada"
          columns={3}
          items={[
            { label: 'Socio', value: result.member.name },
            { label: 'Concepto', value: `${result.concept.code} · ${result.concept.name}` },
            { label: 'Período', value: result.period },
            { label: 'Importe', value: `USD ${result.amount}` },
            { label: 'Estado', value: 'Confirmada' },
            {
              label: 'Respaldo',
              value: `${result.origin.actNumber}${result.origin.ruleCode ? ` · ${result.origin.ruleCode}` : ''}`,
            },
          ]}
        />
      ) : null}
    </section>
  );
}
